/**
 * Team Tab — who supervises this project
 *
 * The project-side view of the same `project_users` rows Settings → Team writes
 * per person. Both exist because admins arrive from both directions: "give this
 * lead their projects" and "who watches this project".
 *
 * Only supervisors appear here, and that is the point rather than an omission.
 * client/admin are org-wide by role and never consult these rows; an agent is
 * bounded by the record they handled, which membership neither widens nor
 * narrows. A supervisor's reach over transcripts and analyses is the one thing
 * this table decides — see `record.read` / `analysis.read` in
 * `packages/core/src/rbac/permissions.ts`.
 */

import { useEffect, useState } from 'react';
import { useReportUnsavedSection } from '@/components/settings/UnsavedChangesGuard';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Users } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { SettingsSaveBar } from '@/components/settings/SettingsSaveBar';
import { Checkbox } from '@/components/ui/checkbox';
import { TableSkeleton } from '@/components/ui/skeletons';
import { cn } from '@/lib/utils';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { logError } from '@/lib/logger';

interface TeamTabProps {
  projectId: string;
}

export function TeamTab({ projectId }: TeamTabProps) {
  const { t } = useTranslation(['projects', 'users', 'common']);
  const { apiClient } = useApiClient();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const queryKey = ['projects', projectId, 'supervisors'] as const;

  const supervisorsQuery = useQuery({
    queryKey,
    queryFn: () => apiClient.projects.getProjectSupervisors(projectId),
  });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Seed the checklist from the server, and re-seed whenever the answer
  // changes — an org switch or another admin's save must not leave the boxes
  // showing a state that is no longer true.
  useEffect(() => {
    const supervisors = supervisorsQuery.data?.supervisors;
    if (!supervisors) return;
    setSelectedIds(supervisors.filter((s) => s.assigned).map((s) => s.id));
  }, [supervisorsQuery.data]);

  // The checklist lives only in this component, so the screen's shared
  // `isDirty` never sees it — and switching sections unmounts the tab. Compare
  // as sets: the order the boxes were ticked in is not an edit.
  const assignedIds = (supervisorsQuery.data?.supervisors ?? [])
    .filter((s) => s.assigned)
    .map((s) => s.id);
  useReportUnsavedSection(
    'project-settings:team',
    assignedIds.length !== selectedIds.length ||
      assignedIds.some((id) => !selectedIds.includes(id))
  );

  const saveMutation = useMutation({
    mutationFn: () => apiClient.projects.updateProjectSupervisors(projectId, selectedIds),
    onSuccess: () => {
      toast.success(t('projects.supervisorsSaved'));
      void queryClient.invalidateQueries({ queryKey });
      // The per-user screen shows a project count for each person; a save here
      // changes it, so its cache cannot be left as it was.
      void queryClient.invalidateQueries({ queryKey: ['settings', 'team'] });
    },
    onError: (error) => {
      logError(
        t('projects.supervisorsSaveFailed'),
        { component: 'TeamTab', projectId },
        error instanceof Error ? error : undefined
      );
      toast.error(
        t('projects.supervisorsSaveFailed'),
        error instanceof Error ? error.message : undefined
      );
    },
  });

  const supervisors = supervisorsQuery.data?.supervisors ?? [];

  if (supervisorsQuery.isLoading) {
    return <TableSkeleton rows={3} />;
  }

  // A failed request must not be reported as an empty organization. Without
  // this branch `data?.supervisors ?? []` rendered the "no supervisors here"
  // card on a 403 or a dropped connection — an admin asking why their
  // supervisor sees nothing would be told the org has no supervisors. Same
  // class of lie as the record panel blaming the pipeline for a permissions
  // failure, which is what started this whole thread.
  if (supervisorsQuery.isError) {
    return (
      <Card className="p-4">
        <div
          className="flex items-center gap-2 text-sm text-danger"
          data-testid="project-team-error"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {t('projects.supervisorsLoadFailed')}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="project-team-tab">
      <Card className="p-4">
        <div className="flex items-start gap-3 mb-4">
          <Users className="h-5 w-5 text-fg-muted shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-fg">
              {t('projects.supervisorsTitle')}
            </h3>
            <p className="text-xs text-fg-muted mt-1">{t('projects.supervisorsHint')}</p>
          </div>
        </div>

        {supervisors.length === 0 ? (
          <div
            className="flex items-center gap-2 rounded-card border border-border p-3 text-xs text-fg-muted"
            data-testid="project-team-empty"
          >
            <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
            {t('projects.supervisorsNoneInOrg')}
          </div>
        ) : (
          <div className="space-y-2" data-testid="project-team-list">
            {supervisors.map((supervisor) => (
              <label
                key={supervisor.id}
                className={cn(
                  'flex items-center gap-3 rounded-card border border-border p-3 transition-colors',
                  supervisor.manageable === false
                    ? 'cursor-default opacity-70'
                    : 'cursor-pointer hover:bg-accent'
                )}
              >
                <Checkbox
                  checked={selectedIds.includes(supervisor.id)}
                  // The project's administrator holds their row by having
                  // created the project, not by oversight granted here. Leaving
                  // the box live would let it be unticked and saved with
                  // nothing happening — the server refuses to touch that row.
                  disabled={supervisor.manageable === false}
                  onCheckedChange={() =>
                    setSelectedIds((current) =>
                      current.includes(supervisor.id)
                        ? current.filter((id) => id !== supervisor.id)
                        : [...current, supervisor.id]
                    )
                  }
                  data-testid={`project-team-checkbox-${supervisor.id}`}
                  aria-label={supervisor.name}
                />
                <div className="min-w-0">
                  <p className="text-sm text-fg truncate">{supervisor.name}</p>
                  <p className="text-xs text-fg-muted truncate">{supervisor.email}</p>
                  {supervisor.manageable === false && (
                    <p className="text-xs text-fg-subtle mt-0.5">
                      {t('projects.supervisorIsProjectAdmin')}
                    </p>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}

        {supervisors.length > 0 && (
          <SettingsSaveBar
            onSave={() => saveMutation.mutate()}
            isSaving={saveMutation.isPending}
            buttonTestId="project-team-save-button"
          />
        )}
      </Card>
    </div>
  );
}
